import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/app/_module/utils/show-toast";
import announcementQueryKeys from "../services/announcement-keys";
import {
  getAnnouncements,
  getAnnouncementDetail,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  acknowledgeAnnouncement,
  getPendingAnnouncements,
  searchAnnouncementAudience,
} from "../services/announcement-services";
import type { CreateAnnouncementRequest, AnnouncementListResponse, AnnouncementDetailResponse, Announcement, AnnouncementAudienceMember } from "../services/announcement-services";

// People under this agency who can be picked as recipients, filtered by what the owner typed
export const useAnnouncementAudience = (agencyId: string, q: string) => {
  return useQuery<AnnouncementAudienceMember[]>({
    queryKey: [...announcementQueryKeys.all, "audience", agencyId, q],
    queryFn: () => searchAnnouncementAudience(agencyId, q),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    retry: 1,
  });
};

// Hook to list announcements for an agency
export const useGetAnnouncements = (agencyId: string, page = 1, limit = 20) => {
  return useQuery<AnnouncementListResponse>({
    queryKey: announcementQueryKeys.list(agencyId, page),
    queryFn: () => getAnnouncements(agencyId, page, limit),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    retry: 1,
    retryDelay: 1000,
  });
};

// Hook to get announcement detail with acknowledgments
export const useGetAnnouncementDetail = (agencyId: string, announcementId: string) => {
  return useQuery<AnnouncementDetailResponse>({
    queryKey: announcementQueryKeys.detail(announcementId),
    queryFn: () => getAnnouncementDetail(agencyId, announcementId),
    enabled: !!agencyId && !!announcementId,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to create announcement
export const useCreateAnnouncement = (agencyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => createAnnouncement(agencyId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: announcementQueryKeys.lists() });
      showToast("Announcement created successfully", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to create announcement", "error");
    },
  });
};

// Hook to update announcement
export const useUpdateAnnouncement = (agencyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ announcementId, data }: { announcementId: string; data: Partial<CreateAnnouncementRequest> }) =>
      updateAnnouncement(agencyId, announcementId, data),
    onSuccess: async (_, { announcementId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: announcementQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: announcementQueryKeys.detail(announcementId) }),
      ]);
      showToast("Announcement updated successfully", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to update announcement", "error");
    },
  });
};

// Hook to delete announcement
export const useDeleteAnnouncement = (agencyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (announcementId: string) => deleteAnnouncement(agencyId, announcementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all });
      showToast("Announcement deleted", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to delete announcement", "error");
    },
  });
};

// Hook to acknowledge announcement
export const useAcknowledgeAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agencyId, announcementId }: { agencyId: string; announcementId: string }) =>
      acknowledgeAnnouncement(agencyId, announcementId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: announcementQueryKeys.pending() }),
        queryClient.invalidateQueries({ queryKey: announcementQueryKeys.lists() }),
      ]);
    },
    onError: () => {
      showToast("Failed to acknowledge announcement", "error");
    },
  });
};

// Hook to get pending announcements (for blocking modal)
export const useGetPendingAnnouncements = () => {
  return useQuery<Announcement[]>({
    queryKey: announcementQueryKeys.pending(),
    queryFn: () => getPendingAnnouncements(),
    staleTime: 60 * 1000, // 1 minute - check more frequently
    refetchOnMount: true,
    refetchOnWindowFocus: true, // Re-check when user comes back to tab
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
